import { createContext, useReducer, useContext, ReactNode, useMemo, Dispatch } from 'react';

// Types
type Timezone = 'UTC' | 'CST';

interface TimezoneState {
	timezone: Timezone;
}

interface SetTimezoneAction {
	type: 'SET_TIMEZONE';
	timezone: Timezone;
}

type TimezoneAction = SetTimezoneAction;

type TimezoneContextType = [TimezoneState, Dispatch<TimezoneAction>];

// Initial State
const initialTimezoneState: TimezoneState = {
	timezone: (localStorage.getItem('timezone') as Timezone) || 'UTC',
};

// Reducer
function timezoneReducer(state: TimezoneState, action: TimezoneAction): TimezoneState {
	if (action.type === 'SET_TIMEZONE') {
		localStorage.setItem('timezone', action.timezone);
		return { ...state, timezone: action.timezone };
	}

	throw new Error(`Unhandled action type: ${action.type}`);
}

// Create context
const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

// Provider
export const TimezoneProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(timezoneReducer, initialTimezoneState);
	const value = useMemo(() => [state, dispatch] as TimezoneContextType, [state, dispatch]);
	return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

// Hook
export const useTimezoneController = () => {
	const context = useContext(TimezoneContext);
	if (!context) {
		throw new Error('useTimezoneController must be used within a TimezoneProvider');
	}
	return context;
};

// Action creator
export const setTimezone = (dispatch: Dispatch<TimezoneAction>, timezone: Timezone) => {
	dispatch({ type: 'SET_TIMEZONE', timezone });
};
